/**
 * DetailPanel (DESIGN.md T2, panel-first decision 2026-10-09): shows the selected list item
 * next to the list. Placement follows §9.1: inline aside ≥ xl, overlay drawer md–xl,
 * full-screen Sheet on phones. Header: prev/next, wider, open as page, close.
 */
import { ChevronLeftIcon, ChevronRightIcon, ExternalLinkIcon, Maximize2Icon, Minimize2Icon, XIcon } from 'lucide-react';
import { useState, type ComponentProps, type ReactNode } from 'react';
import { Tabs } from '../components/tabs';
import { ActionOverflowMenu } from '../actions/action-menu';
import type { ContextMenuSpec } from '../actions/context-menu-area';
import { EntityContextMenu } from '../actions/entity-context-menu';
import { Button } from '../components/button';
import { Sheet, SheetContent, SheetDescription, SheetTitle } from '../components/sheet';
import { BREAKPOINTS, useMediaQuery } from '../hooks/use-media-query';
import { cn } from '../lib/utils';
import { useUiStrings } from '../strings';

export interface DetailPanelProps {
  title: string;
  subtitle?: string;
  /** Avatar or icon in the header. */
  media?: ReactNode;
  status?: ReactNode;
  /** Primary action + "More" menu. */
  actions?: ReactNode;
  /**
   * The entity's actions (`entityMenu`): a "…" button in the panel bar and the context menu of the
   * panel header (right-click, Shift+F10, long-press); the same entries as its list row.
   */
  menu?: ContextMenuSpec;
  onClose: () => void;
  onPrevious?: () => void;
  onNext?: () => void;
  onOpenAsPage?: () => void;
  /** Sticky footer (Save/Cancel while a section edits). */
  footer?: ReactNode;
  /** Shared tab context; only the active body mounts, including in the phone sheet. */
  tabs?: Omit<ComponentProps<typeof Tabs>, 'children' | 'className' | 'asChild'>;
  /** Docked between identity/actions and the sole scrolling body. Use the shared TabsList. */
  navigation?: ReactNode;
  /** Phones: a bottom sheet (peek of an item, e.g. a calendar event) instead of the full-screen side sheet. */
  phoneSheet?: 'bottom';
  children: ReactNode;
}

export function DetailPanel(props: DetailPanelProps) {
  const isWide = useMediaQuery(BREAKPOINTS.xl);
  const isPhone = !useMediaQuery(BREAKPOINTS.md);
  const bottom = isPhone && props.phoneSheet === 'bottom';
  const [wide, setWide] = useState(false);

  if (isWide) {
    return (
      <aside
        aria-label={props.title}
        className={cn('flex shrink-0 flex-col border-l bg-card transition-all duration-200 ease-out', wide ? 'w-panel-wide' : 'w-panel')}
      >
        <DetailPanelContent {...props} wide={wide} onToggleWide={() => setWide(!wide)} />
      </aside>
    );
  }

  return (
    <Sheet open onOpenChange={(open) => !open && props.onClose()}>
      <SheetContent side={bottom ? 'bottom' : 'right'} showCloseButton={false} className={cn('gap-0 bg-card p-0 md:max-w-panel-wide', bottom && 'max-h-[85dvh] rounded-t-xl')}>
        <SheetTitle className="sr-only">{props.title}</SheetTitle>
        <SheetDescription className="sr-only">{props.subtitle ?? props.title}</SheetDescription>
        <DetailPanelContent {...props} />
      </SheetContent>
    </Sheet>
  );
}

function DetailPanelContent({
  title,
  subtitle,
  media,
  status,
  actions,
  menu,
  onClose,
  onPrevious,
  onNext,
  onOpenAsPage,
  footer,
  tabs,
  navigation,
  children,
  wide,
  onToggleWide,
}: DetailPanelProps & { wide?: boolean; onToggleWide?: () => void }) {
  const strings = useUiStrings();
  const hasMenu = menu !== undefined && menu.actions.length > 0;
  const content = (
    <div className="flex min-h-0 flex-1 flex-col" data-slot="detail-panel-frame">
      <div className="flex h-bar shrink-0 items-center gap-1 border-b px-2">
        {onPrevious && (
          <Button variant="ghost" size="icon-sm" aria-label={strings.previous} onClick={onPrevious}>
            <ChevronLeftIcon />
          </Button>
        )}
        {onNext && (
          <Button variant="ghost" size="icon-sm" aria-label={strings.next} onClick={onNext}>
            <ChevronRightIcon />
          </Button>
        )}
        <div className="flex-1" />
        {hasMenu && <ActionOverflowMenu actions={menu.actions} title={menu.title ?? title} size="icon-sm" />}
        {onToggleWide && (
          <Button variant="ghost" size="icon-sm" aria-label={strings.wider} aria-pressed={wide} onClick={onToggleWide}>
            {wide ? <Minimize2Icon /> : <Maximize2Icon />}
          </Button>
        )}
        {onOpenAsPage && (
          <Button variant="ghost" size="icon-sm" aria-label={strings.openAsPage} onClick={onOpenAsPage}>
            <ExternalLinkIcon />
          </Button>
        )}
        <Button variant="ghost" size="icon-sm" aria-label={strings.closeDetails} onClick={onClose}>
          <XIcon />
        </Button>
      </div>
      <EntityContextMenu menu={hasMenu ? { title: menu.title ?? title, actions: menu.actions } : undefined}>
        <div className="flex shrink-0 items-start gap-3 px-4 py-2.5">
          {media}
          <div className="flex min-w-0 flex-1 flex-col gap-0.5">
            <h2 className="truncate font-semibold">{title}</h2>
            {subtitle && <p className="truncate text-sm text-muted-foreground">{subtitle}</p>}
            {status && <div className="pt-1">{status}</div>}
          </div>
        </div>
      </EntityContextMenu>
      {actions && <div className="flex shrink-0 flex-wrap gap-2 px-4 pb-2.5">{actions}</div>}
      {navigation && <div data-slot="detail-panel-navigation" className="shrink-0 px-4">{navigation}</div>}
      <div data-slot="detail-panel-body" className="min-h-0 flex-1 overflow-y-auto overscroll-contain px-4 pb-4">{children}</div>
      {footer && <div className="flex shrink-0 justify-end gap-2 border-t px-4 py-3">{footer}</div>}
    </div>
  );
  return tabs ? <Tabs {...tabs} modalSizing={false} asChild>{content}</Tabs> : content;
}

export interface DescriptionItem {
  label: string;
  value: ReactNode;
}

/** Read-only key/value rows (DESIGN.md §12 DescriptionList), used in panel sections. */
export function DescriptionList({ items, className }: { items: readonly DescriptionItem[]; className?: string }) {
  return (
    <dl className={cn('@container/descriptions flex flex-col gap-2 text-sm', className)}>
      {items.map(({ label, value }) => (
        <div key={label} className="grid min-w-0 grid-cols-1 gap-x-4 gap-y-1 @md/descriptions:grid-cols-3">
          <dt className="text-muted-foreground">{label}</dt>
          <dd className="min-w-0 break-words @md/descriptions:col-span-2">{value}</dd>
        </div>
      ))}
    </dl>
  );
}

/** A titled section inside a panel or settings page. */
export interface SectionProps { title: string; description?: string; actions?: ReactNode; children: ReactNode; className?: string }

export function Section({ title, description, actions, children, className }: SectionProps) {
  return (
    <section className={cn('flex min-w-0 flex-col gap-2.5 border-b py-3 last:border-b-0', className)}>
      <div className="flex items-start justify-between gap-2">
        <div className="flex flex-col gap-0.5">
          <h3 className="text-sm font-semibold">{title}</h3>
          {description && <p className="text-xs text-muted-foreground">{description}</p>}
        </div>
        {actions}
      </div>
      {children}
    </section>
  );
}
